export const UserJob = {
  FOUNDER_OWNER: "founder_owner",
  C_SUITE: "c_suite",
  ECOMMERCE_DIGITAL: "ecommerce_digital",
  MARKETING_EMAIL_SMS_RETENTION: "marketing_email_sms_retention",
  MARKETING_PAID_GROWTH_ACQUISITION: "marketing_paid_growth_acquisition",
  MARKETING_BRAND_CREATIVE: "marketing_brand_creative",
  INVENTORY_PROCUREMENT_FORECASTING: "inventory_procurement_forecasting",
  WAREHOUSE_MANAGER: "warehouse_manager",
  WAREHOUSE_OPERATIONS: "warehouse_operations",
  SHIPPING_CARRIER_OPERATIONS: "shipping_carrier_operations",
  RETURNS_REVERSE_LOGISTICS: "returns_reverse_logistics",
  THIRD_PL_SUPPLY_CHAIN: "third_pl_supply_chain",
  CUSTOMER_EXPERIENCE_SUPPORT: "customer_experience_support",
  FINANCE_ANALYTICS: "finance_analytics",
  TECHNOLOGY_IT_DEVELOPMENT: "technology_it_development",
} as const;

export type UserJobType = (typeof UserJob)[keyof typeof UserJob];

export const UserLevel = {
  FOUNDER_OWNER: "founder_owner",
  C_SUITE: "c_suite",
  VP_HEAD_OF: "vp_head_of",
  DIRECTOR: "director",
  MANAGER_TEAM_LEAD: "manager_team_lead",
  INDIVIDUAL_CONTRIBUTOR: "individual_contributor",
} as const;

export type UserLevelType = (typeof UserLevel)[keyof typeof UserLevel];

export const UserJobLabels: Record<UserJobType, string> = {
  [UserJob.FOUNDER_OWNER]: "Founder / Owner",
  [UserJob.C_SUITE]: "C-Suite (CEO, CMO, COO, etc.)",
  [UserJob.ECOMMERCE_DIGITAL]: "Ecommerce / Digital",
  [UserJob.MARKETING_EMAIL_SMS_RETENTION]: "Marketing: Email / SMS / Retention",
  [UserJob.MARKETING_PAID_GROWTH_ACQUISITION]:
    "Marketing: Paid / Growth / Acquisition",
  [UserJob.MARKETING_BRAND_CREATIVE]: "Marketing: Brand / Creative",
  [UserJob.INVENTORY_PROCUREMENT_FORECASTING]:
    "Inventory Management / Procurement / Forecasting",
  [UserJob.WAREHOUSE_MANAGER]: "Warehouse Manager",
  [UserJob.WAREHOUSE_OPERATIONS]:
    "Warehouse Operations (picker, packer, receiver, etc.)",
  [UserJob.SHIPPING_CARRIER_OPERATIONS]: "Shipping & Carrier Operations",
  [UserJob.RETURNS_REVERSE_LOGISTICS]: "Returns / Reverse Logistics",
  [UserJob.THIRD_PL_SUPPLY_CHAIN]: "3PL / Supply Chain",
  [UserJob.CUSTOMER_EXPERIENCE_SUPPORT]: "Customer Experience / Support",
  [UserJob.FINANCE_ANALYTICS]: "Finance / Analytics",
  [UserJob.TECHNOLOGY_IT_DEVELOPMENT]: "Technology / IT / Development",
};

export const UserLevelLabels: Record<UserLevelType, string> = {
  [UserLevel.FOUNDER_OWNER]: "Founder / Owner",
  [UserLevel.C_SUITE]: "C-Suite (CEO, CMO, COO, etc.)",
  [UserLevel.VP_HEAD_OF]: "VP / Head of",
  [UserLevel.DIRECTOR]: "Director",
  [UserLevel.MANAGER_TEAM_LEAD]: "Manager / Team Lead",
  [UserLevel.INDIVIDUAL_CONTRIBUTOR]: "Individual Contributor",
};

export const UserJobOrder = [
  UserJob.FOUNDER_OWNER,
  UserJob.C_SUITE,
  UserJob.ECOMMERCE_DIGITAL,
  UserJob.MARKETING_EMAIL_SMS_RETENTION,
  UserJob.MARKETING_PAID_GROWTH_ACQUISITION,
  UserJob.MARKETING_BRAND_CREATIVE,
  UserJob.INVENTORY_PROCUREMENT_FORECASTING,
  UserJob.WAREHOUSE_MANAGER,
  UserJob.WAREHOUSE_OPERATIONS,
  UserJob.SHIPPING_CARRIER_OPERATIONS,
  UserJob.RETURNS_REVERSE_LOGISTICS,
  UserJob.THIRD_PL_SUPPLY_CHAIN,
  UserJob.CUSTOMER_EXPERIENCE_SUPPORT,
  UserJob.FINANCE_ANALYTICS,
  UserJob.TECHNOLOGY_IT_DEVELOPMENT,
] as const;

// Compile-time check: adding a new UserJob value without updating UserJobOrder causes a type error here.
type _JobOrderExhaustive = [UserJobType] extends [(typeof UserJobOrder)[number]]
  ? true
  : never;
const _assertJobOrderExhaustive: _JobOrderExhaustive = true;
void _assertJobOrderExhaustive;

export const UserLevelOrder = [
  UserLevel.FOUNDER_OWNER,
  UserLevel.C_SUITE,
  UserLevel.VP_HEAD_OF,
  UserLevel.DIRECTOR,
  UserLevel.MANAGER_TEAM_LEAD,
  UserLevel.INDIVIDUAL_CONTRIBUTOR,
] as const;

// Compile-time check: adding a new UserLevel value without updating UserLevelOrder causes a type error here.
type _LevelOrderExhaustive = [UserLevelType] extends [
  (typeof UserLevelOrder)[number],
]
  ? true
  : never;
const _assertLevelOrderExhaustive: _LevelOrderExhaustive = true;
void _assertLevelOrderExhaustive;
